import type { AwsRum } from 'aws-rum-web';

import { OBSERVABILITY_RUNTIME } from './runtime';
import { browserEventSink, setTelemetrySink } from './telemetry';
import { TelemetryEvent, TelemetrySink } from './types';

interface CloudWatchRumConfig {
  appMonitorId: string;
  endpoint: string;
  identityPoolId: string;
  region: string;
  sessionSampleRate: number;
}

type CloudWatchRumClient = Pick<AwsRum, 'recordEvent'>;

function runtimeValue(value: string | undefined): string | undefined {
  const normalized = value?.trim();
  return normalized || undefined;
}

function sampleRate(value: string | undefined): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : 0.1;
}

export function resolveCloudWatchRumConfig(
  environment: string = OBSERVABILITY_RUNTIME.environment
): CloudWatchRumConfig | undefined {
  if (environment !== 'prod') return undefined;

  const appMonitorId = runtimeValue(import.meta.env.VITE_CLOUDWATCH_RUM_APP_MONITOR_ID);
  const identityPoolId = runtimeValue(import.meta.env.VITE_CLOUDWATCH_RUM_IDENTITY_POOL_ID);
  const region = runtimeValue(import.meta.env.VITE_CLOUDWATCH_RUM_REGION);

  if (!appMonitorId || !identityPoolId || !region) return undefined;

  return {
    appMonitorId,
    endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
    identityPoolId,
    region,
    sessionSampleRate: sampleRate(import.meta.env.VITE_CLOUDWATCH_RUM_SESSION_SAMPLE_RATE),
  };
}

function rumEventData(event: TelemetryEvent): object {
  return {
    ...event.fields,
    ...event.context,
    environment: event.runtime.environment,
    service: event.runtime.service,
    timestamp: event.timestamp,
    version: event.runtime.version,
  };
}

function rumEventMetadata(event: TelemetryEvent): Record<string, string> {
  return {
    correlationId: event.context.correlationId,
    ...(event.context.userId ? { userId: event.context.userId } : {}),
  };
}

export function createCloudWatchRumSink(client: CloudWatchRumClient, fallback: TelemetrySink = browserEventSink): TelemetrySink {
  return event => {
    try {
      client.recordEvent(event.name, rumEventData(event), rumEventMetadata(event));
    } catch {
      fallback(event);
    }
  };
}

export async function initializeCloudWatchRum(): Promise<AwsRum | undefined> {
  const config = resolveCloudWatchRumConfig();
  if (!config) return undefined;

  const pending: TelemetryEvent[] = [];
  let activeSink: TelemetrySink = event => pending.push(event);
  setTelemetrySink(event => activeSink(event));

  try {
    const { AwsRum } = await import('aws-rum-web');
    const client = new AwsRum(config.appMonitorId, OBSERVABILITY_RUNTIME.version, config.region, {
      allowCookies: false,
      disableAutoPageView: true,
      enableXRay: false,
      endpoint: config.endpoint,
      identityPoolId: config.identityPoolId,
      sessionSampleRate: config.sessionSampleRate,
      signing: true,
      telemetries: [],
    });
    activeSink = createCloudWatchRumSink(client);
    pending.splice(0).forEach(activeSink);
    return client;
  } catch {
    activeSink = browserEventSink;
    pending.splice(0).forEach(activeSink);
    return undefined;
  }
}
